import TornadoGPTOfflineFreightCalculationDisplay from "../QuotePricePage/TornadoGPTOfflineFreightCalculationDisplay";
import { useState, useEffect, useCallback, useContext } from "react";
import { generateTornadoGPTOfflineDescription } from "../../Services/tornadoGPTOfflineDescriptionService";
import {
    Container,
    Paper,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    Typography,
    Box,
    FormControl,
    InputLabel,
    CircularProgress,
    Snackbar,
    Alert,
    Fade,
    Card,
    CardContent,
    Divider,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import ForgeViewer from "../designToolPage/forgeViewer";
import apiClient from "../../Helpers/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../Helpers/auth-context";
import Dynamics365NavigationBar from "./Dynamics365NavigationBar";
import useAutoLogout from "../../Helpers/useAutoLogout";
import { useQuote } from "./useQuote";
import BackToCPQButton from "../../Components/backToCPQButton";
import { useSignalR } from "../../Contexts/SignalRContext";
import { useTornadoGPTOfflineState, WORKFLOW_STAGES } from "../../Hooks/useTornadoGPTOfflineState";
import AddressAutocomplete from "../../Components/AddressAutocomplete";

const STAGE_LABELS = ["Calculate Price", "Generate Model", "Download & Quote"];

const PriceBreakdown = ({ priceData }) => {
    if (!priceData) return null;

    const items = [
        { label: "Tank Base", value: priceData.tankPrice },
        { label: "Screen Price", value: priceData.screenPrice },
        { label: "Box Price", value: priceData.boxPrice },
        { label: "Freight", value: priceData.freightCost },
    ];

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Price Breakdown
                </Typography>
                {items.map(({ label, value }) => (
                    <Box
                        key={label}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2">{label}:</Typography>
                        <Typography variant="body2">${value?.toFixed(2) || "0.00"}</Typography>
                    </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6" color="primary">
                        Total:
                    </Typography>
                    <Typography variant="h6" color="primary">
                        ${priceData.totalPrice?.toFixed(2) || "0.00"}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const DownloadButtons = ({ onDownload, disabled, workItemId }) => {
    const fileTypes = ["pdf", "dwg1", "dwg2", "dwg3", "dwg3d"];

    return (
        <Box>
            {fileTypes.map((fileType) => (
                <Button
                    key={fileType}
                    fullWidth
                    variant="outlined"
                    onClick={() => onDownload(null, fileType)}
                    disabled={disabled || !workItemId}
                    sx={{ mt: 1 }}
                >
                    Download {fileType.toUpperCase()}
                </Button>
            ))}
        </Box>
    );
};

const Dynamics365TornadoGPTOffline = () => {
    const [TankFreightResult, setTankFreightResult] = useState(null);
    const [BoxFreightResult, setBoxFreightResult] = useState(null);

    const { state } = useContext(AuthContext);
    const { isConnected: isSignalRConnected, connectionId } = useSignalR();
    const location = useLocation();
    const navigate = useNavigate();
    const { addProduct } = useQuote();

    // Local state
    const [QuoteId, setQuoteId] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
    const [isAddingToQuote, setIsAddingToQuote] = useState(false);
    const [inletSizes, setInletSizes] = useState([600, 750, 900, 1050, 1200, 1350, 1500, 1850, 2100]);
    const [errors, setErrors] = useState({ fslAndInletIL: "", riser: "" });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showNotification = useCallback((message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // Use the enhanced state hook
    const {
        currentStage,
        activeStep,
        isGenerating,
        totalPrice,
        priceBreakdown,
        viewerPath,
        downloadUrls,
        modelConfig,
        setIsGenerating,
        setTotalPrice,
        setPriceBreakdown,
        setModelConfig,
        moveToGenerateStage,
        resetWorkflow,
        restoreStateFromLocalStorage,
        saveStateToLocalStorage,
        isSignalRConnected: stateSignalRConnected,
        signalRConnectionId,
    } = useTornadoGPTOfflineState(QuoteId, quantity, showNotification);

    // Constants
    const modelTypes = [
        "TGPTO.130.912",
        "TGPTO.200.1015",
        "TGPTO.320.1218",
        "TGPTO.465.1520",
        "TGPTO.730.1824",
        "TGPTO.1090.2128",
        "TGPTO.1200.2131",
        "TGPTO.1430.2530",
        "TGPTO.1600.2532",
        "TGPTO.1900.3032",
    ];

    useAutoLogout({ isAuthenticated: state.isAuthenticated });

    // Helper functions for workflow control
    const isStage = (stage) => currentStage === stage;
    const canInteractWithInputs = () => isStage(WORKFLOW_STAGES.CALCULATE_PRICE) && !isCalculatingPrice;
    const canCalculatePrice = () => isStage(WORKFLOW_STAGES.CALCULATE_PRICE) && !isCalculatingPrice;
    const canGenerateModel = () => isStage(WORKFLOW_STAGES.GENERATE_MODEL) && !isGenerating && totalPrice > 0;
    const canDownloadAndQuote = () => isStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);

    // Prevent spacebar scrolling
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.code === "Space" && !["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)) {
                event.preventDefault();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Initialize and check auth
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("id");

        console.log("TornadoGPTOffline: Initializing with QuoteId from URL:", id);
        if (id) {
            setQuoteId(id);
        }

        if (!state.isAuthenticated) {
            navigate("/login", {
                state: { returnTo: location.pathname + location.search },
            });
        } else {
            const restored = restoreStateFromLocalStorage();
            if (restored) {
                showNotification("Previous session restored", "info");
            }
        }
    }, [location, state.isAuthenticated, navigate, restoreStateFromLocalStorage, showNotification]);

    // Show SignalR connection status
    useEffect(() => {
        if (isSignalRConnected) {
            console.log("SignalR connected in TornadoGPTOffline");
        } else {
            console.log("SignalR disconnected in TornadoGPTOffline");
        }
    }, [isSignalRConnected]);

    function getInletConfig(modelConfig) {
        return {
            inletSize: modelConfig.inletSize || null,
            inletQty: modelConfig.inletQuantity || 1,
        };
    }

    const handleFreightPriceOverride = (newFreightPrice) => {
        setPriceBreakdown((prev) => ({
            ...prev,
            freightPrice: newFreightPrice,
            totalPrice: prev.totalPrice - prev.freightPrice + newFreightPrice,
        }));
    };

    const buildApiPayload = () => {
        const { inletSize, inletQty } = getInletConfig(modelConfig);

        var data = {
            browserConnectionId: connectionId,
            params: {
                TGPTModel: modelConfig.modelType,
                FSL: modelConfig.finishSurfaceLevel * 100,
                Invert: modelConfig.inletIL * 100,
                InletSize: inletSize / 10,
                InletQty: inletQty,
                WeirOritantaion: modelConfig.weirLayout,
            },
        };
        console.log("TornadoGPTOffline: API Payload Built:", JSON.stringify(data, null, 2));

        return data;
    };

    // Calculate price
    const calculatePrice = useCallback(async () => {
        const { finishSurfaceLevel, inletIL, inletSize, inletQuantity, weirLayout, city, modelType } =
            modelConfig;

        let newErrors = { fslAndInletIL: "", riser: "" };

        if (isNaN(finishSurfaceLevel) || isNaN(inletIL)) {
            newErrors.fslAndInletIL = "Finish Surface Level and Inlet Invert Levels are required";
        }

        if (!inletSize) {
            newErrors.fslAndInletIL = "Please select inlet size";
        }

        if (!inletQuantity || inletQuantity < 1 || inletQuantity > 6) {
            newErrors.fslAndInletIL = "Inlet quantity must be between 1 and 6";
        }

        if (!weirLayout) {
            newErrors.fslAndInletIL = "Please select weir layout";
        }

        setErrors(newErrors);
        if (newErrors.fslAndInletIL) return;

        setIsCalculatingPrice(true);
        try {
            const data = {
                Model: modelType,
                FinishSurfaceLevel: finishSurfaceLevel,
                InletIL: inletIL,
                inletSize: inletSize,
                inletQuantity: inletQuantity,
                weirLayout: weirLayout,
                FreightLocation: city,
            };

            const response = await apiClient.post("api/tornado-gpt-offline/price-calculation", data, {
                headers: { "Content-Type": "application/json" },
            });
            console.log("Price calculation response:", response);

            if (response.data) {
                setTotalPrice(response.data.totalPrice);
                setPriceBreakdown(response.data);
                setBoxFreightResult(response.data.boxCalculationResult);
                setTankFreightResult(response.data.tankCalculationResult);
                showNotification("Price calculated successfully! You can now generate the model.");
                moveToGenerateStage();
            }
        } catch (err) {
            console.error("Price calculation error:", err);
            showNotification("Failed to calculate price", "error");
        } finally {
            setIsCalculatingPrice(false);
        }
    }, [modelConfig, showNotification, moveToGenerateStage, setTotalPrice, setPriceBreakdown]);
    // Handlers
    const handleModelTypeChange = useCallback(
        async (event) => {
            const value = event.target.value;
            setModelConfig((prev) => ({ ...prev, modelType: value }));
        },
        [setModelConfig]
    );

    const handleConnectionSizeChange = useCallback(
        async (event) => {
            const value = event.target.value;
            setModelConfig((prev) => ({ ...prev, connectionSize: value }));
        },
        [setModelConfig]
    );

    const handleConfigChange = useCallback(
        (updates) => {
            setModelConfig((prev) => ({ ...prev, ...updates }));
        },
        [setModelConfig]
    );

    const handleGenerate = useCallback(async () => {
        if (!connectionId) {
            showNotification("SignalR connection not established", "error");
            return;
        }

        setIsGenerating(true);
        saveStateToLocalStorage();

        const data = buildApiPayload();

        try {
            const response = await apiClient.post(
                "/api/aps/designautomation/TornadoGPTOffline/workitems",
                data
            );
            if (response.status !== 200) throw new Error("Failed to generate model");

            showNotification(
                "Model generation started. You can navigate away and come back later to see the results.",
                "info"
            );
        } catch (err) {
            console.error("Model generation error:", err);
            setIsGenerating(false);
            showNotification("Failed to generate model", "error");
        }
    }, [connectionId, buildApiPayload, showNotification, saveStateToLocalStorage, setIsGenerating]);

    const handleDownload = useCallback(
        async (url, fileType) => {
            try {
                console.log("Download attempt:", { url, fileType, workItemId: modelConfig.workItemId });

                if (!modelConfig.workItemId) {
                    showNotification("WorkItem ID not available. Please generate the model first.", "error");
                    return;
                }

                const response = await apiClient.get(
                    `/api/aps/designautomation/tornadoGPTofflinedownload/${fileType}`,
                    {
                        params: { workitemId: modelConfig.workItemId },
                        responseType: "blob",
                        headers: {
                            "Content-Type": "application/octet-stream",
                            Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
                        },
                    }
                );

                const extensions = {
                    pdf: ".pdf",
                    dwg: ".dwg",
                    dwg3d: "._3D.dwg",
                };
                // const filename = `TornadoGPT_${modelConfig.modelType}${
                //     extensions[fileType] || `.${fileType}`
                // }`;
                const filename = `TornadoGPT_${modelConfig.modelType}.${
                    fileType.startsWith("dwg") ? "dwg" : fileType
                }`;

                const blob = new Blob([response.data], {
                    type: response.headers["content-type"] || "application/octet-stream",
                });

                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);

                showNotification("File downloaded successfully");
            } catch (err) {
                console.error("Download error:", err);
                showNotification(`Error downloading file: ${err.message}`, "error");
            }
        },
        [showNotification, modelConfig.modelType, modelConfig.workItemId]
    );

    // Add to CPQ quote
    const addToCPQQuote = async () => {
        setIsAddingToQuote(true);

        if (!totalPrice || totalPrice <= 0) {
            showNotification("Please calculate price first", "error");
            setIsAddingToQuote(false);
            return;
        }

        try {
            // const detailDescription = [
            //     `• Model: ${modelConfig.modelType}`,
            //     `• Finish Surface Level: ${modelConfig.finishSurfaceLevel}m`,
            //     `• Inlet IL: ${modelConfig.inletIL}m`,
            //     `• Inlet Size: ${modelConfig.inletSize}mm`,
            //     `• Inlet Quantity: ${modelConfig.inletQuantity}`,
            //     `• Weir Layout: ${modelConfig.weirLayout}`,
            //     `• Freight Location: ${modelConfig.city}`,
            //     modelConfig.workItemId && `• Work Item ID: ${modelConfig.workItemId}`,
            // ]
            //     // Change the separator from " | " to the newline character "\n"
            //     .filter(Boolean)
            //     .join("\n");
            const detailDescription = generateTornadoGPTOfflineDescription(modelConfig);

            const productData = {
                workItemId: modelConfig.workItemId || null,
                quoteId: QuoteId || null,
                productType: "TornadoGPTOffline",
                modelConfig: {
                    ...modelConfig,
                    configurationSummary: `${modelConfig.modelType} - ${modelConfig.city}`,
                },
                priceBreakdown: priceBreakdown ? { ...priceBreakdown } : null,
                totalPrice: totalPrice,
                productName: `TornadoGPT ${modelConfig.modelType}`,
                partNumber: modelConfig.modelType,
                pcs_DetailDescriptions: detailDescription,
                quantity: quantity,
                price: totalPrice,
            };

            addProduct(productData);
            showNotification(`TornadoGPTOffline ${modelConfig.modelType} added to CPQ quote!`, "success");
            setTimeout(() => {
                resetWorkflow();
                setIsAddingToQuote(false);
            }, 1500);
        } catch (error) {
            console.error("Add to quote error:", error);
            showNotification(`Failed to add product: ${error.message}`, "error");
            setIsAddingToQuote(false);
        }
    };
    return (
        <Container>
            <Dynamics365NavigationBar />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STAGE_LABELS.map((label, index) => (
                        <Step key={label} completed={activeStep > index}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "center", alignItems: "center" }}>
                    <Typography variant="caption" color={isSignalRConnected ? "success.main" : "error.main"}>
                        SignalR: {isSignalRConnected ? "Connected" : "Disconnected"}
                    </Typography>
                    {isGenerating && (
                        <Typography variant="caption" color="info.main">
                            Model generation in progress...
                        </Typography>
                    )}
                </Box>

                {currentStage !== WORKFLOW_STAGES.CALCULATE_PRICE && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={resetWorkflow}
                            disabled={isGenerating || isCalculatingPrice || isAddingToQuote}
                        >
                            Start New Configuration
                        </Button>
                    </Box>
                )}
            </Paper>

            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Paper
                        sx={{
                            p: 3,
                            mt: 3,
                            elevation: 4,
                            border: "1px solid #ddd",
                            boxShadow: 1,
                        }}
                    >
                        <Fade in={true}>
                            <Box>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Model Type</InputLabel>
                                    <Select
                                        value={modelConfig.modelType}
                                        onChange={handleModelTypeChange}
                                        disabled={!canInteractWithInputs()}
                                    >
                                        {modelTypes.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            type="number"
                                            label="Finish Surface Level (m)"
                                            value={modelConfig.finishSurfaceLevel}
                                            onChange={(e) =>
                                                handleConfigChange({
                                                    finishSurfaceLevel: parseFloat(e.target.value),
                                                })
                                            }
                                            error={!!errors.fslAndInletIL}
                                            disabled={!canInteractWithInputs()}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            type="number"
                                            label="Inlet IL (m)"
                                            value={modelConfig.inletIL}
                                            onChange={(e) =>
                                                handleConfigChange({
                                                    inletIL: parseFloat(e.target.value),
                                                })
                                            }
                                            error={!!errors.fslAndInletIL}
                                            disabled={!canInteractWithInputs()}
                                        />
                                    </Grid>
                                </Grid>

                                {errors.fslAndInletIL && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ mt: 1, display: "block" }}
                                    >
                                        {errors.fslAndInletIL}
                                    </Typography>
                                )}

                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Inlet Size (mm)</InputLabel>
                                    <Select
                                        value={modelConfig.inletSize || ""}
                                        onChange={(e) =>
                                            handleConfigChange({ inletSize: parseInt(e.target.value) })
                                        }
                                        disabled={inletSizes.length === 0 || !canInteractWithInputs()}
                                    >
                                        {inletSizes.length === 0 ? (
                                            <MenuItem disabled>Loading...</MenuItem>
                                        ) : (
                                            inletSizes.map((size) => (
                                                <MenuItem key={size} value={size}>
                                                    {size}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Inlet Quantity</InputLabel>
                                    <Select
                                        value={modelConfig.inletQuantity ?? 1} // Use nullish coalescing instead of ||
                                        onChange={(e) =>
                                            handleConfigChange({ inletQuantity: e.target.value })
                                        }
                                        disabled={!canInteractWithInputs()}
                                    >
                                        <MenuItem value={1}>1</MenuItem>
                                        <MenuItem value={2}>2</MenuItem>
                                        <MenuItem value={3}>3</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Weir Layout</InputLabel>
                                    <Select
                                        value={modelConfig.weirLayout || ""}
                                        onChange={(e) => handleConfigChange({ weirLayout: e.target.value })}
                                        disabled={!canInteractWithInputs()}
                                    >
                                        <MenuItem value="Left">Left</MenuItem>
                                        <MenuItem value="Right">Right</MenuItem>
                                    </Select>
                                </FormControl>

                                <Grid item xs={12}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography variant="body1" color="text.secondary" sx={{}}>
                                            Destination Address
                                        </Typography>
                                    </Box>
                                    <FormControl fullWidth>
                                        <AddressAutocomplete onAddressSelect={setModelConfig} />
                                    </FormControl>
                                </Grid>

                                {/* <FormControl fullWidth margin="normal">
                                    <InputLabel>Freight Location</InputLabel>
                                    <Select
                                        value={modelConfig.freightLocationType}
                                        onChange={(e) =>
                                            handleConfigChange({
                                                freightLocationType: e.target.value,
                                            })
                                        }
                                        disabled={!canInteractWithInputs()}
                                    >
                                        {freightLocations.map((location) => (
                                            <MenuItem key={location} value={location}>
                                                {location}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl> */}

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={calculatePrice}
                                    disabled={!canCalculatePrice()}
                                    sx={{ mt: 2 }}
                                >
                                    {isCalculatingPrice ? <CircularProgress size={24} /> : "Calculate Price"}
                                </Button>
                                <TornadoGPTOfflineFreightCalculationDisplay
                                    tankFreightResult={TankFreightResult}
                                    boxFreightResult={BoxFreightResult}
                                    onFreightPriceOverride={handleFreightPriceOverride}
                                />

                                <PriceBreakdown priceData={priceBreakdown} />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleGenerate}
                                    disabled={!canGenerateModel()}
                                    sx={{
                                        mt: 2,
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                        "&:hover": {
                                            backgroundColor: "#1565c0",
                                        },
                                        "&:disabled": {
                                            backgroundColor: "#e0e0e0",
                                            color: "#9e9e9e",
                                        },
                                    }}
                                >
                                    {isGenerating ? <CircularProgress size={24} /> : "Generate Model"}
                                </Button>

                                <DownloadButtons
                                    onDownload={handleDownload}
                                    disabled={!canDownloadAndQuote()}
                                    workItemId={modelConfig.workItemId}
                                />
                            </Box>
                        </Fade>
                    </Paper>

                    <Paper
                        sx={{
                            p: 3,
                            mt: 3,
                            elevation: 4,
                            border: "1px solid #ddd",
                            boxShadow: 1,
                        }}
                    >
                        <TextField
                            label="Product Quantity"
                            type="number"
                            disabled={!canDownloadAndQuote()}
                            value={quantity}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                                    setQuantity(value === "" ? 1 : parseInt(value));
                                }
                            }}
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 1, step: 1 }}
                            sx={{ width: 140, marginTop: 3 }}
                            error={quantity < 1}
                            helperText={quantity < 1 ? "Quantity must be at least 1" : ""}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            onClick={addToCPQQuote}
                            disabled={
                                !canDownloadAndQuote() || isAddingToQuote || !totalPrice || quantity < 1
                            }
                            sx={{ mt: 2 }}
                        >
                            {isAddingToQuote ? (
                                <CircularProgress size={24} />
                            ) : (
                                `Add to CPQ Quote - $${totalPrice?.toLocaleString() || "0"}`
                            )}
                        </Button>
                    </Paper>
                </Grid>

                <Grid item xs={8}>
                    <Paper sx={{ p: 2, height: "85vh" }}>
                        <ForgeViewer url={viewerPath} loading={isGenerating} />
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
                <BackToCPQButton />
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Dynamics365TornadoGPTOffline;
